import { useEffect, useMemo, useState } from "react"
import type { Timestamp } from "@/types/date"
import type { NeedsAttentionItem, TripDisruption, TripRef } from "@/types/trip"

import { nowMillis } from "@/lib/date-utils"
import { assistantSuggestionsApi } from "./assistant-suggestions-api"
import { baseApi } from "./base-api"
import { useFeatureFlag } from "./feature-flag-api"

export interface GetNeedsAttentionResponse {
  disruptions: TripDisruption[]
}

const EMPTY_DISRUPTIONS: TripDisruption[] = []
const MAX_TIMEOUT_MS = 2_147_483_647

export const needsAttentionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNeedsAttention: builder.query<GetNeedsAttentionResponse, void>({
      query: () => ({ url: "/api/trips/needs-attention" }),
      providesTags: ["NeedsAttention"],
    }),

    getPastNeedsAttention: builder.query<GetNeedsAttentionResponse, TripRef>({
      query: (tripRef) => ({
        url: `/api/trips/${tripRef}/needs-attention/past`,
      }),
      providesTags: ["NeedsAttention"],
    }),

    // Marking done is per item but invalidates the whole list: a row belongs to the trip, so
    // settling it changes what every surface shows, not just the one that was tapped.
    markNeedsAttentionItemDone: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/trips/needs-attention/${id}/done`,
        method: "POST",
      }),
      invalidatesTags: ["NeedsAttention"],
      // The home card renders its rows from the suggestion source, a separate slice whose tags
      // do not cross. Without this, settling a row from the home slide-out leaves it on screen.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled
        void dispatch(
          assistantSuggestionsApi.util.invalidateTags(["AssistantSuggestions"])
        )
      },
    }),
  }),
})

export const {
  useGetNeedsAttentionQuery,
  useGetPastNeedsAttentionQuery,
  useMarkNeedsAttentionItemDoneMutation,
} = needsAttentionApi

/**
 * The clock the expiry sweeps run on. The server stops serving what has expired, but a cached
 * queue outlives it — a page left open must not keep offering a card or a row whose moment has
 * passed. Shared with the home suggestion queue.
 *
 * It wakes on the soonest deadline still ahead rather than on a fixed interval, so a surface
 * with nothing pending re-renders never, and one with a row due in an hour re-renders once.
 */
export function useSweepClock(
  deadlines: (Timestamp | null | undefined)[]
): number {
  const [now, setNow] = useState(() => nowMillis())

  const next = deadlines.reduce<number | null>((soonest, deadline) => {
    if (!deadline) return soonest
    const at = deadline.epoch_millis
    if (at <= now) return soonest
    return soonest === null || at < soonest ? at : soonest
  }, null)

  useEffect(() => {
    if (next === null) return
    // setTimeout takes a 32-bit delay; anything longer silently fires on the next tick and
    // spins. A deadline past the cap re-arms instead, which costs one wake every ~24 days.
    const delay = Math.min(Math.max(next - nowMillis(), 0), MAX_TIMEOUT_MS)
    const timer = setTimeout(() => setNow(nowMillis()), delay)
    return () => clearTimeout(timer)
  }, [next, now])

  return now
}

/** A null deadline never passes: whatever it belongs to is retired by its source's liveness. */
export function isPast(
  deadline: Timestamp | null | undefined,
  now: number
): boolean {
  if (!deadline) return false
  return deadline.epoch_millis <= now
}

/**
 * Past the window its kind of plan earns. Gated on `actionable_until_timestamp` rather than on
 * `missed_title`, which the server sends for every row including ones that can never miss.
 */
export function missed(item: NeedsAttentionItem, now: number): boolean {
  return isPast(item.actionable_until_timestamp, now)
}

/**
 * Open disruptions keyed by trip, in the order the server sent them (worst and soonest
 * first). The server stops serving expired rows, but a cached list outlives them — so
 * rows are swept against their own `expires_at_timestamp` here and a disruption whose last row
 * expires disappears without waiting for a refetch.
 */
export function useTripDisruptions(): {
  byTrip: Map<TripRef, TripDisruption[]>
  byKey: Map<string, TripDisruption>
} {
  // Rows only exist for flight-alert users, so everyone else skips both the read and the clock.
  const enabled = useFeatureFlag("flight_alerts")
  const { data } = useGetNeedsAttentionQuery(undefined, { skip: !enabled })
  const served = data?.disruptions ?? EMPTY_DISRUPTIONS
  const now = useSweepClock(
    served.flatMap((disruption) =>
      disruption.items.map((item) => item.expires_at_timestamp)
    )
  )

  return useMemo(() => {
    const byTrip = new Map<TripRef, TripDisruption[]>()
    const byKey = new Map<string, TripDisruption>()
    for (const disruption of served) {
      const items = disruption.items.filter(
        (item) => !isPast(item.expires_at_timestamp, now)
      )
      if (items.length === 0) continue

      const live = { ...disruption, items }
      byKey.set(live.key, live)
      const existing = byTrip.get(disruption.trip_ref)
      if (existing) existing.push(live)
      else byTrip.set(disruption.trip_ref, [live])
    }
    return { byTrip, byKey }
  }, [served, now])
}
